/**
 * The finite SYMBOLIC pair policy inventory.
 *
 * theme-customization A1. Every record carries a stable ID, a scope, a floor/policy and
 * SYMBOLIC colour operands — a token name, a fixed literal, or a foreground/alpha/background
 * composite whose leaves are themselves operands. Operands are resolved from the candidate
 * mode BEFORE compositing, so an override reaches a composited case's real dependencies.
 *
 * The rule this file exists to obey, from the plan verbatim: "Do not copy the current resolved
 * `fg`/`bg` hexes or infer dependencies by equal hex values: today's case builders erase the
 * token dependencies of their computed composites." Nothing below stores a resolved hex except
 * the four genuinely fixed literals (#FFFFFF media/badge foregrounds and the two media
 * backdrops), each of which is a code-owned physical value rather than a token.
 *
 * WHERE THE IDS COME FROM. The defining inventory is the union of `ALL_CASES` and
 * `CHART_CONTRAST_RECORDS` in `apps/web/src/styles/__tests__/contrast-cases.ts` plus the two
 * on-accent foreground families in `apps/web/src/components/__tests__/portal-contrast.test.tsx`,
 * as they stand AFTER A0 (six chrome IDs re-pointed to `--chrome-gradient-end`, ten added).
 * The generators below mirror the web builders row for row so the IDs match exactly;
 * `apps/web/src/styles/__tests__/theme-pair-coverage.test.ts` proves the parity independently.
 * This file does NOT import from web — shared must stay web-free.
 *
 * THREE ADDITIONS the plan mandates on top of that inventory, and which therefore have no
 * counterpart in `ALL_CASES` today:
 *   - `on-accent/*`      the two delegated on-accent foreground families (8 records).
 *   - `on-accent-hover/*` "Add on-accent hover pairs at 90% over card/page/muted" (24).
 *   - `gradient/*`       "For gradients, measure the minimum over the actual sRGB
 *                        interpolation … endpoints alone are insufficient for arbitrary
 *                        editable colours" (8 — see `gradientRecords()` for why it is eight
 *                        rather than six). At stock the minimum coincides with the far stop,
 *                        which is why the endpoint rows alone were sufficient before both stops
 *                        became editable roles.
 *
 * A2a HAS NOW LANDED and this file is the POST-SPLIT state (the note here used to say the
 * opposite, correctly, while A1 was the head). What it changed, all under the stable-ID rule —
 * every ID below is the one A1 shipped:
 *   - the on-accent operands became `--on-accent` / `--on-accent-initials` (`ON_ACCENT_FOREGROUNDS`);
 *   - the four text/fill splits became foreground-only (`TEXT_OPERAND`);
 *   - the checked switch track became the derived `--switch-checked`;
 *   - `::selection` became `--on-accent-initials`.
 * TWO IDs were ADDED, with the consumer that renders them: the checked switch track's boundary
 * against a card and against the page (`data-[state=checked]:border-switch-checked-boundary`).
 * 356 records at A1, 358 now.
 */

import { AA_NON_TEXT, AA_TEXT } from './contrast.js';
import type { ThemeTokenId } from './theme.js';

// ─── Operand and policy shapes ───────────────────────────────────────────────

export type ColourOperand =
  | { token: ThemeTokenId }
  | { hex: string }
  | { composite: { foreground: ColourOperand; alpha: number; background: ColourOperand } };

/** A two-stop sRGB ramp. Only ever a BACKGROUND, and only under a `gradient-minimum` policy. */
export type ColourGradient = { gradient: { from: ColourOperand; to: ColourOperand } };

export type PairPolicy =
  | { kind: 'ratio'; floor: number }
  | { kind: 'gradient-minimum'; floor: number }
  | { kind: 'chart-separation'; floor: number; boundary: ColourOperand; cue: string };

export type ThemePairScope = 'portal' | 'report';

export type ThemePairRecord = {
  id: string;
  scope: ThemePairScope;
  foreground: ColourOperand;
  background: ColourOperand | ColourGradient;
  policy: PairPolicy;
  note: string;
};

const t = (token: ThemeTokenId): ColourOperand => ({ token });
const lit = (hex: string): ColourOperand => ({ hex });

/**
 * theme-customization A2a — THE TEXT/FILL OPERAND SPLIT.
 *
 * `tailwind.config.ts`'s `extend.textColor` re-points five `text-*` utilities at a readable
 * alias while the same names in `extend.colors` keep the DEEP value for backgrounds and
 * borders. So a case whose ID names one of these tokens measures a DIFFERENT token depending on
 * which side of the pair it sits: `bg-status-approved-text` is still #0E6027 carrying white,
 * `text-status-approved-text` is now `--status-approved-readable`. Every ID below is unchanged;
 * only the foreground OPERAND moves, which is what keeps a B1 dark arm able to lighten the
 * badge foreground without touching the action fill.
 *
 * This map is the PORT of `apps/web/src/styles/__tests__/contrast-classification.ts`'s
 * `textAlias` entries. Shared must stay web-free, so it is copied rather than imported, and
 * `theme-pair-coverage.test.ts` compares the two operand-for-operand.
 *
 * All FIVE keys are listed, including `--status-review`, which no record uses as a foreground
 * today: the web map has the row, and a map that is a subset of its counterpart is a divergence
 * waiting for the first B1 case that needs it rather than a simplification.
 */
export const TEXT_OPERAND: Partial<Record<ThemeTokenId, ThemeTokenId>> = {
  '--status-approved-text': '--status-approved-readable',
  '--status-changes-text': '--status-changes-readable',
  '--status-rejected': '--status-rejected-text',
  '--status-review': '--status-review-text',
  '--report-accent': '--ai-text',
};

/**
 * The operand a FOREGROUND written as `operand` actually resolves to. Backgrounds never use it.
 *
 * ONE helper, not two: this was `tx(token)` plus `txOf(operand)` where `tx(x) === txOf(t(x))`,
 * i.e. the same function written twice with the callers split arbitrarily between them.
 *
 * The `'token' in operand` branch is TYPE NARROWING the `ColourOperand` union requires, not
 * optional flexibility — a literal and a composite both pass through unchanged. A composite
 * passing through is the case `theme.test.ts`'s "no foreground composite hides a split token"
 * guards: it is correct only while no foreground is a composite over a split token, and that
 * precondition is asserted rather than assumed.
 */
const txOf = (operand: ColourOperand): ColourOperand =>
  'token' in operand ? t(TEXT_OPERAND[operand.token] ?? operand.token) : operand;
const over = (
  foreground: ColourOperand,
  alpha: number,
  background: ColourOperand,
): ColourOperand => ({ composite: { foreground, alpha, background } });
const ratio = (floor: number): PairPolicy => ({ kind: 'ratio', floor });

/** Every token leaf of an operand / gradient / policy. Alpha and backdrop leaves included. */
function operandLeaves(operand: ColourOperand | ColourGradient, into: ThemeTokenId[]): void {
  if ('token' in operand) {
    if (!into.includes(operand.token)) into.push(operand.token);
    return;
  }
  if ('hex' in operand) return;
  if ('gradient' in operand) {
    operandLeaves(operand.gradient.from, into);
    operandLeaves(operand.gradient.to, into);
    return;
  }
  operandLeaves(operand.composite.foreground, into);
  operandLeaves(operand.composite.background, into);
}

/**
 * The complete token dependency set of a record: foreground leaves, background leaves
 * (including every composite/backdrop leaf and both gradient stops) and the chart boundary.
 */
export function pairTokenLeaves(record: ThemePairRecord): ThemeTokenId[] {
  const leaves: ThemeTokenId[] = [];
  operandLeaves(record.foreground, leaves);
  operandLeaves(record.background, leaves);
  if (record.policy.kind === 'chart-separation') operandLeaves(record.policy.boundary, leaves);
  return leaves;
}

// ─── The derived role/surface lists (copied from CLASSIFICATION's flags) ──────

/**
 * `GENERAL_SURFACES` (9) and `REUSABLE_TEXT_ROLES` (14) are derived on the web side from
 * `CLASSIFICATION`'s `generalSurface` / `reusableText` flags
 * (`contrast-classification.ts:830-836`). Copied here in DECLARATION ORDER so the generated
 * `(c)` IDs come out in the same order as well as the same set. A0 pinned both counts.
 */
export const GENERAL_SURFACES: readonly ThemeTokenId[] = [
  '--background',
  '--card',
  '--popover',
  '--secondary',
  '--muted',
  '--accent',
  '--bg-light',
  '--bg-card',
  '--bg-surface-light',
];

/** The card surfaces a chart is drawn on, i.e. the grounds of `charts/chart-theme.ts`'s ink. */
export const CHART_INK_CANVASES: readonly ThemeTokenId[] = ['--card', '--bg-card'];

export const REUSABLE_TEXT_ROLES: readonly ThemeTokenId[] = [
  '--foreground',
  '--muted-foreground',
  '--text-primary',
  '--text-body',
  '--content-secondary',
  '--content-muted',
  '--status-draft-text',
  '--status-pending-text',
  '--status-approved-text',
  '--status-changes-text',
  '--status-review-text',
  '--status-rejected',
  '--status-abandoned-text',
  '--status-backup-text',
];

/** The four portal accents. */
export const PORTAL_ACCENTS: readonly ThemeTokenId[] = [
  '--ss-yellow',
  '--ss-blue',
  '--ss-pink',
  '--ss-purple',
];

/**
 * `DARK_CHROME` (`contrast-registry.ts:276-297`). After A0 the gradient far stop is the
 * `--chrome-gradient-end` TOKEN, not a `#2D2D35` literal — which is what makes an admin edit
 * to that role reach these measurements.
 */
const DARK_CHROME_GROUNDS: readonly { token: ThemeTokenId; consumer: string }[] = [
  { token: '--ss-black', consumer: 'TopNavBar.tsx:229 gradient near stop' },
  { token: '--chrome-gradient-end', consumer: 'TopNavBar.tsx:229 gradient far stop' },
  { token: '--chrome-bg', consumer: 'TopNavBar.tsx:559 mobile drawer nav' },
  {
    token: '--chrome-bg-active',
    consumer: 'TopNavBar.tsx:582,:612,:635 active mobile-navigation link fill',
  },
];

/** The report-owned tokens. A record whose every leaf is one of these is `report` scope. */
const REPORT_OWNED = /^--report-(ink|subtle|tint|avatar-\d+-(bg|text))$/;

/**
 * Report-only records whose CONSUMER is a report section even though one operand is a token the
 * portal also uses. Scope follows the consumer, not token ownership: `--report-accent` is shared
 * (Phase 8 moved portal AI copy onto it), but `--report-tint` is only ever painted by
 * `intel-report/sections/ExecutiveSummarySection.tsx:50`. Its portal sibling
 * (`(d) --report-accent on a card`, `ai/AiGapList.tsx:98`) stays `portal` and keeps its case,
 * which is the plan's "Shared report/portal tokens such as AI purple retain their portal cases".
 */
const REPORT_SCOPE_IDS: ReadonlySet<string> = new Set(['(d) --report-accent on the tint wash']);

function scopeOf(record: Omit<ThemePairRecord, 'scope'>): ThemePairScope {
  if (REPORT_SCOPE_IDS.has(record.id)) return 'report';
  const leaves = pairTokenLeaves({ ...record, scope: 'portal' });
  return leaves.length > 0 && leaves.every((leaf) => REPORT_OWNED.test(leaf)) ? 'report' : 'portal';
}

function record(
  id: string,
  foreground: ColourOperand,
  background: ColourOperand | ColourGradient,
  policy: PairPolicy,
  note: string,
): ThemePairRecord {
  const partial = { id, foreground, background, policy, note };
  return { ...partial, scope: scopeOf(partial) };
}

// ─── (a) shadcn sibling pairs ────────────────────────────────────────────────

const SIBLING_PAIRS: readonly [ThemeTokenId, ThemeTokenId][] = [
  ['--foreground', '--background'],
  ['--card-foreground', '--card'],
  ['--popover-foreground', '--popover'],
  ['--primary-foreground', '--primary'],
  ['--secondary-foreground', '--secondary'],
  ['--muted-foreground', '--muted'],
  ['--accent-foreground', '--accent'],
  ['--destructive-foreground', '--destructive'],
];

function siblingPairRecords(): ThemePairRecord[] {
  return SIBLING_PAIRS.map(([fg, bg]) =>
    record(`(a) ${fg} on ${bg}`, t(fg), t(bg), ratio(AA_TEXT), 'shadcn sibling pair'),
  );
}

// ─── (b) status badge pairs ──────────────────────────────────────────────────

/** `rejected` has no `-text` sibling: its fill doubles as its text. */
const STATUS_BADGES: readonly [ThemeTokenId, ThemeTokenId][] = [
  ['--status-draft-text', '--status-draft-bg'],
  ['--status-pending-text', '--status-pending-bg'],
  ['--status-approved-text', '--status-approved-bg'],
  ['--status-changes-text', '--status-changes-bg'],
  ['--status-review-text', '--status-review-bg'],
  ['--status-rejected', '--status-rejected-bg'],
  ['--status-abandoned-text', '--status-abandoned-bg'],
  ['--status-backup-text', '--status-backup-bg'],
];

function statusBadgeRecords(): ThemePairRecord[] {
  return STATUS_BADGES.map(([fg, bg]) =>
    record(`(b) ${fg} on ${bg}`, txOf(t(fg)), t(bg), ratio(AA_TEXT), 'status badge'),
  );
}

// ─── (c) reusable text roles × every general surface ─────────────────────────

function crossSurfaceRecords(): ThemePairRecord[] {
  const out: ThemePairRecord[] = [];
  for (const role of REUSABLE_TEXT_ROLES) {
    for (const surface of GENERAL_SURFACES) {
      out.push(
        record(
          `(c) ${role} on ${surface}`,
          txOf(t(role)),
          t(surface),
          ratio(AA_TEXT),
          'cross-surface — name pairing proves nothing for a reusable utility',
        ),
      );
    }
  }
  return out;
}

// ─── (d) the rendered consumer inventory ─────────────────────────────────────

type InventoryRow = readonly [
  id: string,
  foreground: ColourOperand,
  background: ColourOperand,
  floor: number,
  note: string,
];

function chromeStateRows(): InventoryRow[] {
  return [
    ['default muted chrome item on the lightest gradient endpoint', t('--chrome-text-muted'), t('--chrome-gradient-end'), AA_TEXT, 'TopNavBar default dark-navigation item; worst gradient endpoint'], // prettier-ignore
    ['identity chrome text on the lightest gradient endpoint', t('--chrome-text'), t('--chrome-gradient-end'), AA_TEXT, 'TopNavBar user identity; worst gradient endpoint'], // prettier-ignore
    ['white label on the opaque chrome hover', t('--on-chrome'), t('--chrome-bg-hover'), AA_TEXT, 'dark trigger and contextual-toolbar hover'], // prettier-ignore
    ['white label on the soft chrome hover', t('--on-chrome'), t('--chrome-bg-hover-soft'), AA_TEXT, 'desktop and mobile navigation hover'], // prettier-ignore
    ['muted chrome text on the soft chrome hover', t('--chrome-text-muted'), t('--chrome-bg-hover-soft'), AA_TEXT, 'default item remains readable while its dark hover fill is applied'], // prettier-ignore
    ['highlight focus ring on the soft chrome hover', t('--highlight'), t('--chrome-bg-hover-soft'), AA_NON_TEXT, 'dark navigation focus state'], // prettier-ignore
    ['highlight focus ring on the active chrome fill', t('--highlight'), t('--chrome-bg-active'), AA_NON_TEXT, 'active mobile-navigation focus state'], // prettier-ignore
    ...PORTAL_ACCENTS.map(
      (accent) =>
        [
          `${accent} active label on the semantic chrome fill`,
          t(accent),
          t('--chrome-bg-active'),
          AA_TEXT,
          'active mobile-navigation label',
        ] as InventoryRow,
    ),
    // NOT on-chrome: CandidateBulkActionBar's embedded dark control, which keeps a fixed white.
    ['white text in an embedded dark control', lit('#FFFFFF'), t('--chrome-bg'), AA_TEXT, 'contextual dark toolbar control'], // prettier-ignore
    ['interactive boundary on an embedded dark control', t('--input'), t('--chrome-bg'), AA_NON_TEXT, 'contextual dark toolbar control boundary'], // prettier-ignore
    ['highlight focus ring on an embedded dark control', t('--highlight'), t('--chrome-bg'), AA_NON_TEXT, 'contextual dark toolbar control focus state'], // prettier-ignore
    // A0 re-pointed these four panels from a `#FFFFFF` literal to `--popover`: same value,
    // same IDs, and a palette edit to the popover surface now reaches these measurements.
    ['white navigation-menu default text', t('--content-secondary'), t('--popover'), AA_TEXT, 'TopNavBar bespoke navigation dropdown default item'], // prettier-ignore
    ['white navigation-menu active text', t('--text-primary'), t('--popover'), AA_TEXT, 'TopNavBar bespoke navigation dropdown active item'], // prettier-ignore
    ['white navigation-menu muted icon', t('--content-muted'), t('--popover'), AA_TEXT, 'TopNavBar bespoke navigation dropdown icon'], // prettier-ignore
    ['navigation-menu default text on its focus fill', t('--content-secondary'), t('--bg-surface-light'), AA_TEXT, 'TopNavBar bespoke navigation dropdown focused default item'], // prettier-ignore
    ['navigation-menu active text on its focus fill', t('--text-primary'), t('--bg-surface-light'), AA_TEXT, 'TopNavBar bespoke navigation dropdown focused active item'], // prettier-ignore
    ['navigation-menu focus ring on white', t('--ring'), t('--popover'), AA_NON_TEXT, 'TopNavBar.tsx:354 bespoke navigation focus state; :483,:494,:504 Radix user-menu items carry focus-visible:ring-ring'], // prettier-ignore
    ['navigation-menu focus ring on its focus fill', t('--ring'), t('--bg-surface-light'), AA_NON_TEXT, 'TopNavBar bespoke navigation dropdown focus state'], // prettier-ignore
    ['Radix user-menu default text', t('--popover-foreground'), t('--popover'), AA_TEXT, 'TopNavBar Radix user dropdown default item'], // prettier-ignore
    ['Radix user-menu focused text', t('--accent-foreground'), t('--accent'), AA_TEXT, 'TopNavBar.tsx:481-508 Radix user dropdown focused item'], // prettier-ignore
    ['Radix user-menu muted metadata', t('--content-muted'), t('--popover'), AA_TEXT, 'TopNavBar Radix user dropdown email and icons'], // prettier-ignore
  ];
}

function identityTextRows(): InventoryRow[] {
  const rows: InventoryRow[] = [];
  for (let i = 1; i <= 8; i += 1) {
    rows.push([
      `shared avatar ${i} initials on its hash background`,
      t(`--avatar-hash-${i}-text` as ThemeTokenId),
      t(`--avatar-hash-${i}-bg` as ThemeTokenId),
      AA_TEXT,
      'components/data-display/Avatar.tsx ordered hash palette',
    ]);
  }
  for (let i = 1; i <= 6; i += 1) {
    rows.push([
      `report avatar ${i} initials on its hash background`,
      t(`--report-avatar-${i}-text` as ThemeTokenId),
      t(`--report-avatar-${i}-bg` as ThemeTokenId),
      AA_TEXT,
      'features/intel-report/primitives/ReportAvatar.tsx ordered hash palette',
    ]);
  }
  rows.push(
    ['pink tag foreground on its tag background', t('--tag-pink-text'), t('--tag-pink-bg'), AA_TEXT, 'CreatorsModule.tsx and ShortlistSummary.tsx'], // prettier-ignore
    ['cyan tag foreground on its tag background', t('--tag-cyan-text'), t('--tag-cyan-bg'), AA_TEXT, 'CreatorsModule.tsx and ShortlistSummary.tsx'], // prettier-ignore
    ['admin portal foreground on the pending tint', t('--portal-admin-foreground'), t('--status-pending-bg'), AA_TEXT, 'FeedbackComment.tsx admin role chip'], // prettier-ignore
    ['client portal foreground on the review tint', t('--portal-client-foreground'), t('--status-review-bg'), AA_TEXT, 'FeedbackComment.tsx client role chip'], // prettier-ignore
    ['influencer portal foreground on the pink tag tint', t('--portal-influencer-foreground'), t('--tag-pink-bg'), AA_TEXT, 'FeedbackComment.tsx influencer role chip'], // prettier-ignore
    ['agent portal foreground on the 20% agent tint', t('--portal-agent-foreground'), over(t('--ss-purple'), 0.2, t('--card')), AA_TEXT, 'FeedbackComment.tsx agent role chip, bg-portal-agent/20 over card'], // prettier-ignore
    ['Later source foreground on the 15% client tint', t('--post-source-later'), over(t('--ss-blue'), 0.15, t('--card')), AA_TEXT, 'PostSourceBadge.tsx Later badge'], // prettier-ignore
    ['Approval source foreground on the 60% influencer tint', t('--post-source-approval'), over(t('--ss-pink'), 0.6, t('--card')), AA_TEXT, 'PostSourceBadge.tsx Approval badge'], // prettier-ignore
  );
  return rows;
}

function inventoryRows(): InventoryRow[] {
  const rows: InventoryRow[] = [...chromeStateRows(), ...identityTextRows()];

  rows.push(
    ['submission fallback initials on the gradient start', t('--ss-black'), t('--submission-avatar-start'), AA_TEXT, 'SubmissionHeader.tsx fallback avatar gradient'], // prettier-ignore
    ['submission fallback initials on the gradient end', t('--ss-black'), t('--submission-avatar-end'), AA_TEXT, 'SubmissionHeader.tsx fallback avatar gradient'], // prettier-ignore
    ['influencer identity boundary on the pink role-chip surface', t('--feedback-influencer-accent'), t('--tag-pink-bg'), AA_NON_TEXT, 'FeedbackComment.tsx influencer role chip'], // prettier-ignore
    ['tooltip-labelled Instagram glyph on a card', t('--instagram-soft'), t('--card'), AA_NON_TEXT, 'CreatorDataTable.tsx Instagram glyph; adjacent tooltip supplies its name'], // prettier-ignore
  );

  // Phase 8 contrast-debt repairs. Base fills and /90 hovers are separate rendered states.
  for (const [name, fill] of [
    ['approved action', '--status-approved-text'],
    ['changes action', '--status-changes-text'],
    ['AI action', '--report-accent'],
  ] as const) {
    rows.push([
      `white on the deep ${name} fill`,
      lit('#FFFFFF'),
      t(fill),
      AA_TEXT,
      'Phase 8 action base',
    ]);
    for (const surface of ['--card', '--background', '--muted'] as const) {
      rows.push([
        `white on the deep ${name} /90 hover over ${surface}`,
        lit('#FFFFFF'),
        over(t(fill), 0.9, t(surface)),
        AA_TEXT,
        'Phase 8 action hover composite',
      ]);
    }
  }

  for (const surface of ['--background', '--muted'] as const) {
    rows.push([
      `--report-accent small copy on ${surface}`,
      t('--report-accent'),
      t(surface),
      AA_TEXT,
      'Phase 8 repaired small AI copy',
    ]);
  }
  for (const alpha of [0.05, 0.1, 0.13] as const) {
    rows.push([
      `--report-accent small copy on ai-purple/${alpha * 100} over a card`,
      t('--report-accent'),
      over(t('--ai-purple'), alpha, t('--card')),
      AA_TEXT,
      'Phase 8 repaired small AI copy on its actual wash',
    ]);
  }
  rows.push([
    '--report-accent small copy on ai-purple/13 over a raised panel',
    t('--report-accent'),
    over(t('--ai-purple'), 0.13, t('--muted')),
    AA_TEXT,
    'Phase 8 worst shared AI wash',
  ]);

  for (const surface of GENERAL_SURFACES) {
    rows.push([
      `--ring over ${surface}`,
      t('--ring'),
      t(surface),
      AA_NON_TEXT,
      'ui/button.tsx:7 focus-visible:ring-ring',
    ]);
  }
  // B1 — THE FOCUS INDICATOR THAT ACTUALLY TOUCHES AN ACCENT FILL. The outer ring is `--ring`
  // over `ring-offset-background`, so it never meets the fill (the rows above measure it against
  // the surrounds it does meet). What sits ON the accent is the fixed dark inset indicator the
  // four portal variants carry, `ui/button.tsx` `focus-visible:shadow-[inset_…--on-accent-hsl…]`.
  // The ID is stable, the floor is unchanged, and the operand is now the band that is rendered
  // there — which is what makes this measurable in dark, where the editable ring is white.
  for (const accent of PORTAL_ACCENTS) {
    rows.push([
      `--ring on the ${accent} fill`,
      t('--on-accent'),
      t(accent),
      AA_NON_TEXT,
      'ui/button.tsx portal variants: the inset focus indicator drawn on the accent fill',
    ]);
  }
  // B1 GATE — THE CHART BAND MEASURED AGAINST ITS CANVAS. `chartRecords()` below measures series
  // against the card and against each other; NOTHING measured either chart constant against the
  // surface it is stroked on, so an ink that vanished on a dark card was invisible to the whole
  // instrument. `CHART_INK_COLOR` is the band whose ground is the card (the area curve
  // substitute, the donut leader, the sentiment leader); the on-fill boundary keeps its own
  // token and its own rows inside the chart policy.
  for (const surface of CHART_INK_CANVASES) {
    rows.push([
      `--chart-ink on ${surface}`,
      t('--chart-ink'),
      t(surface),
      AA_NON_TEXT,
      'charts/AreaChart.tsx curve substitute, charts/DonutChart.tsx leader, ' +
        'ai-report/SentimentSection.tsx leader',
    ]);
  }
  for (const surface of GENERAL_SURFACES) {
    rows.push([
      `--input boundary on ${surface}`,
      t('--input'),
      t(surface),
      AA_NON_TEXT,
      'ui/input.tsx:15, ui/select.tsx:18, ui/textarea.tsx:19, ui/button.tsx:14',
    ]);
  }

  rows.push(
    ['switch thumb on the unchecked track', t('--highlight'), t('--input'), AA_NON_TEXT, 'ui/switch.tsx thumb bg-highlight'], // prettier-ignore
    // A2a re-pointed the checked track from `--primary` to the derived `--switch-checked`; the
    // ID is stable because it is the same rendered pair. The derivation keeps `interactive`
    // attributable — see `DERIVED_TOKEN_SOURCES`.
    ['switch thumb on the checked track', t('--highlight'), t('--switch-checked'), AA_NON_TEXT, 'ui/switch.tsx thumb bg-highlight on data-[state=checked]:bg-switch-checked'], // prettier-ignore
    ['unchecked switch track on the page', t('--input'), t('--background'), AA_NON_TEXT, 'ui/switch.tsx:11'], // prettier-ignore
    ['unchecked switch track on a card', t('--input'), t('--card'), AA_NON_TEXT, 'ui/switch.tsx:11'], // prettier-ignore
    // NEW IN A2a, with its consumer: the checked track now paints a boundary
    // (`data-[state=checked]:border-switch-checked-boundary`), which is what will make a black
    // track visible on a near-black card once B1 ships the dark arm. In light it is the same
    // colour as the track and the two rows below simply record that the checked control is
    // still distinguishable from the surface it sits on.
    ['checked switch track boundary on a card', t('--switch-checked-boundary'), t('--card'), AA_NON_TEXT, 'ui/switch.tsx data-[state=checked]:border-switch-checked-boundary'], // prettier-ignore
    ['checked switch track boundary on the page', t('--switch-checked-boundary'), t('--background'), AA_NON_TEXT, 'ui/switch.tsx data-[state=checked]:border-switch-checked-boundary'], // prettier-ignore
    ['checkbox boundary on a card', t('--primary'), t('--card'), AA_NON_TEXT, 'ui/checkbox.tsx:13 border-primary'], // prettier-ignore
    ['checkbox mark on its checked fill', t('--primary-foreground'), t('--primary'), AA_TEXT, 'ui/checkbox.tsx:13'], // prettier-ignore
    ['hover:bg-primary/90 over a card, with its foreground', t('--primary-foreground'), over(t('--primary'), 0.9, t('--card')), AA_TEXT, 'ui/button.tsx:12'], // prettier-ignore
    ['hover:bg-destructive/90 over a card, with its foreground', t('--destructive-foreground'), over(t('--destructive'), 0.9, t('--card')), AA_TEXT, 'ui/button.tsx:13'], // prettier-ignore
    ['hover:bg-secondary/80 over a card, with its foreground', t('--secondary-foreground'), over(t('--secondary'), 0.8, t('--card')), AA_TEXT, 'ui/button.tsx:15'], // prettier-ignore
    ['hover:bg-accent over a card, with its foreground', t('--accent-foreground'), t('--accent'), AA_TEXT, 'ui/button.tsx:14'], // prettier-ignore
    // A2b re-pointed this foreground from `--ss-black` (the editable chrome role) to the
    // code-owned `--on-accent`; #1a1a1a -> #111827, so the measurement moves 13.34 -> 13.60.
    // The pair ID is DELIBERATELY unchanged (it is a stable key, not a description).
    // Evidence is named by its class recipe rather than a line number, because the previous
    // `:471` cite is exactly what went stale when A2b edited that line.
    ['hover:bg-ss-yellow-light with text-ss-black', t('--on-accent'), t('--ss-yellow-light'), AA_TEXT, 'admin/components/ReviewQueueTable.tsx empty-state link: bg-ss-yellow text-on-accent hover:bg-ss-yellow-light'], // prettier-ignore
    ['white on the rejected solid fill', lit('#FFFFFF'), t('--status-rejected'), AA_TEXT, 'StatusBadge solid variant'], // prettier-ignore
    ['ProgressBar default fill on its semantic muted track', t('--status-approved'), t('--muted'), AA_NON_TEXT, 'ProgressBar.tsx:19 default fill on the bg-muted track at :34'], // prettier-ignore
    // A2a re-pointed this foreground from `--ss-black` (the editable `chrome` role) to
    // `--on-accent-initials`; same #1A1A1A, and `globals.css`'s `::selection` already reads the
    // new token. The two count pills still spell it `text-ss-black` and move to the same role in
    // A2b — identical value either way, so the measurement is the same on both sides of that batch.
    ['the ::selection / selected-count-pill text on the lime fill', t('--on-accent-initials'), t('--highlight'), AA_TEXT, 'globals.css ::selection; the selected tab count pill in the agent + influencer SubmissionsPage'], // prettier-ignore
    ['--report-subtle on the ink band', t('--report-subtle'), t('--report-ink'), AA_TEXT, 'intel-report/sections/ReportHeaderSection.tsx:46'], // prettier-ignore
    ['white on the ink band', lit('#FFFFFF'), t('--report-ink'), AA_TEXT, 'intel-report/sections/ReportHeaderSection.tsx:26'], // prettier-ignore
    ['--report-accent on the tint wash', t('--report-accent'), t('--report-tint'), AA_TEXT, 'intel-report/sections/ExecutiveSummarySection.tsx:50'], // prettier-ignore
    ['--report-accent on a card', t('--report-accent'), t('--card'), AA_TEXT, 'ai/AiGapList.tsx:98'], // prettier-ignore
    ['--ai-purple icon/bar on a card', t('--ai-purple'), t('--card'), AA_NON_TEXT, 'ai/AiScoreRing.tsx; admin/components/PostCard.tsx:265-266 opaque white backplate'], // prettier-ignore
    ['--ai-purple icon/bar on the page', t('--ai-purple'), t('--background'), AA_NON_TEXT, 'ai/AiInsightsPanel.tsx:74'], // prettier-ignore
    ['text-destructive on its own /10 wash over a card', t('--destructive'), over(t('--destructive'), 0.1, t('--card')), AA_TEXT, 'admin/components/ai-report/CreateDashboardDialog.tsx:334 role="alert"'], // prettier-ignore
    ['text-destructive on a card', t('--destructive'), t('--card'), AA_TEXT, 'ChannelEditForm.tsx, AudienceDataToggle.tsx, ClientHubDriftPanel.tsx and ~20 more'], // prettier-ignore
    ['text-destructive on the page', t('--destructive'), t('--background'), AA_TEXT, 'same consumers, on the page canvas'], // prettier-ignore
  );

  for (const surface of ['--card', '--background', '--muted', '--secondary'] as const) {
    rows.push([
      `text-primary (the link variant) on ${surface}`,
      t('--primary'),
      t(surface),
      AA_TEXT,
      'ui/button.tsx:16 link variant; CampaignForm.tsx:201; ContentRightsBlock.tsx:221',
    ]);
  }

  for (const [status, pct] of [
    ['approved', 0.1],
    ['pending', 0.1],
    ['changes', 0.1],
    ['review', 0.1],
  ] as const) {
    rows.push([
      `--status-${status}-text on the bg-status-${status}/${pct * 100} tint over a card`,
      t(`--status-${status}-text` as ThemeTokenId),
      over(t(`--status-${status}` as ThemeTokenId), pct, t('--card')),
      AA_TEXT,
      'ai/AiConfidenceBadge.tsx:13,:19,:24, ai/AiFeedbackSuggestion.tsx:21-22',
    ]);
  }

  // A0 FIXED MEDIA. The operand that matters is the disk's actual composite — 90% white over
  // the creator's own frame — and the two backdrops bracket every frame a video can have
  // (the mix is monotone in the backdrop channel). Neither token is editable.
  for (const [media, backdrop] of [
    ['black media', '#000000'],
    ['white media', '#FFFFFF'],
  ] as const) {
    rows.push([
      `fixed play glyph on the 90% fixed-white disk over ${media}`,
      t('--fixed-dark'),
      over(t('--fixed-white'), 0.9, lit(backdrop)),
      AA_NON_TEXT,
      'media/VideoPlayer.tsx:180-181 and client/components/ContentThumbnailCard.tsx:85,:88',
    ]);
  }

  // A0's three portal white-ALPHA surfaces: real surfaces, resolved through `--bg-card`, each
  // registered with its REAL backdrop and the foreground it actually inherits.
  rows.push(
    ['AiFeedbackSuggestion key-point chip: secondary text on card/60 over the AI wash', t('--secondary-foreground'), over(t('--card'), 0.6, over(t('--ai-purple'), 0.05, t('--card'))), AA_TEXT, 'ai/AiFeedbackSuggestion.tsx:98 Badge variant="secondary" on the :58 ai-purple/5 card'], // prettier-ignore
    ['StructuredBriefContent creator example: brief body on card/50 over the pending tint', t('--status-pending-text'), over(t('--card'), 0.5, t('--status-pending-bg')), AA_TEXT, 'features/_shared/StructuredBriefContent.tsx:203 inside CampaignBriefSection.tsx:122'], // prettier-ignore
    ['StructuredBriefContent creator example: label on card/50 over the pending tint', t('--content-secondary'), over(t('--card'), 0.5, t('--status-pending-bg')), AA_TEXT, 'features/_shared/StructuredBriefContent.tsx:207 "Example N" label on the same box'], // prettier-ignore
  );

  for (const [chip, fill, text] of [
    ['draft', t('--status-draft-bg'), '--status-draft-text'],
    ['relevant', t('--status-approved-bg'), '--status-approved-text'],
    ['needs-review', t('--status-pending-bg'), '--status-pending-text'],
    ['not-relevant', t('--status-abandoned-bg'), '--status-abandoned-text'],
    ['heroes', over(t('--ai-purple'), 0.1, t('--card')), '--report-accent'],
  ] as const) {
    rows.push([
      `PostsToolbar count pill on card/60 over the ${chip} active chip`,
      t(text),
      over(t('--card'), 0.6, fill),
      AA_TEXT,
      'admin/components/PostsToolbar.tsx:237 inherits the :223 active chip foreground',
    ]);
  }

  // DARK CHROME (R3d). The indicator on this bar is the component ring the four nav controls
  // carry, measured at 1.4.11's 3:1; the bar's own label operand is `--on-chrome` (A0), and the
  // inline portal accent is the active item's colour. The IDs are stable across both changes.
  for (const ground of DARK_CHROME_GROUNDS) {
    rows.push([
      `nav focus ring (lime) on ${ground.token}`,
      t('--highlight'),
      t(ground.token),
      AA_NON_TEXT,
      `${ground.consumer}; TopNavBar.tsx dark-bar focus ring`,
    ]);
    rows.push([
      `text-white on ${ground.token}`,
      t('--on-chrome'),
      t(ground.token),
      AA_TEXT,
      `${ground.consumer}; the bar's own label colour`,
    ]);
    for (const accent of PORTAL_ACCENTS) {
      rows.push([
        `${accent} active-nav label on ${ground.token}`,
        t(accent),
        t(ground.token),
        AA_TEXT,
        `${ground.consumer}; TopNavBar.tsx style={{ color: config.accentCssValue }}`,
      ]);
    }
  }

  return rows;
}

function inventoryRecords(): ThemePairRecord[] {
  // The FOREGROUND goes through the A2a text/fill split, the background never does — the same
  // asymmetry `contrast-cases.ts`'s `c()` applies, and the reason `bg-status-approved-text`
  // stays a deep fill while `text-status-approved-text` does not.
  return inventoryRows().map(([id, fg, bg, floor, note]) =>
    record(`(d) ${id}`, txOf(fg), bg, ratio(floor), note),
  );
}

// ─── (e) surface-vs-surface state pairs ──────────────────────────────────────

const TAB_STATE = 'Radix tab selected vs track';
const TAB_CONSUMER = 'ui/tabs.tsx:38 data-[state=active]:bg-card + data-[state=active]:ring-input';

function statePairRecords(): ThemePairRecord[] {
  return [
    record(`(e) ${TAB_STATE} — distinguisher vs the unselected track`, t('--input'), t('--muted'), ratio(AA_NON_TEXT), TAB_CONSUMER), // prettier-ignore
    record(`(e) ${TAB_STATE} — distinguisher vs the selected fill`, t('--input'), t('--card'), ratio(AA_NON_TEXT), TAB_CONSUMER), // prettier-ignore
    record(`(e) ${TAB_STATE} — FOCUSED indicator vs its own offset band`, t('--ring'), t('--background'), ratio(AA_NON_TEXT), `${TAB_CONSUMER} — ring-ring over ring-offset-background`), // prettier-ignore
    record(`(e) ${TAB_STATE} — FOCUSED indicator vs the unselected track`, t('--ring'), t('--muted'), ratio(AA_NON_TEXT), `${TAB_CONSUMER} — the focused-active ring is drawn over the track`), // prettier-ignore
  ];
}

// ─── on-accent: the two delegated foreground families ────────────────────────

/**
 * `portal-contrast.test.tsx`'s two on-accent families, against all four editable accents. These
 * delegated checks sit OUTSIDE `ALL_CASES`, which is exactly why they are named as their own
 * defining set.
 *
 * Each row is `[idToken, operand]` — the stable-ID rule this file's header states, applied: the
 * ID still spells `--text-primary` / `--ss-black`, the operand is the code-owned role the CSS
 * actually paints.
 */
const ON_ACCENT_FOREGROUNDS: readonly (readonly [ThemeTokenId, ThemeTokenId])[] = [
  ['--text-primary', '--on-accent'],
  ['--ss-black', '--on-accent-initials'],
];

/** The surfaces a portal-accent button's `/90` hover composites over. */
const ON_ACCENT_HOVER_SURFACES: readonly ThemeTokenId[] = ['--card', '--background', '--muted'];

function onAccentRecords(): ThemePairRecord[] {
  const out: ThemePairRecord[] = [];
  for (const accent of PORTAL_ACCENTS) {
    for (const [idToken, operand] of ON_ACCENT_FOREGROUNDS) {
      out.push(
        record(
          `on-accent/${accent}/${idToken}`,
          t(operand),
          t(accent),
          ratio(AA_TEXT),
          'ui/button.tsx portal variants; Avatar.tsx and TopNavBar.tsx initials',
        ),
      );
      for (const surface of ON_ACCENT_HOVER_SURFACES) {
        out.push(
          record(
            `on-accent-hover/${accent}/${idToken}/${surface}`,
            t(operand),
            over(t(accent), 0.9, t(surface)),
            ratio(AA_TEXT),
            'the portal variants’ /90 hover state, composited over its real surface',
          ),
        );
      }
    }
  }
  return out;
}

// ─── chart separation ────────────────────────────────────────────────────────

const CHART_CUE =
  'line/area: persistent outlined marker and dash; bar: visible category label; ' +
  'donut: direct label with leader line';

/**
 * `CHART_CONTRAST_RECORDS`. The chart tokens themselves are code-owned, but `--card` is an
 * editable surface, so the canvas can move under a fixed series. Every record carries the same
 * policy — a direct 3:1 pass, OR the existing black boundary clearing 3:1 against BOTH
 * neighbours together with the bound non-colour cue. There is no admin-controlled exception.
 *
 * B1 REPOINTED THE BOUNDARY from `--chart-1` to `--chart-boundary`. Same value in light, so no
 * light measurement moves; in dark `--chart-1` is #FFFFFF and the separator cannot be series 1
 * any more. `chart-theme.ts`'s `CHART_BOUNDARY_COLOR` was moved onto the same token in the same
 * batch, so the policy names the band the charts actually stroke.
 *
 * The boundary this policy names is the ON-FILL one only. The bands drawn on the CANVAS — the
 * area curve substitute and the donut leader that this policy's own cue string names — take
 * `--chart-ink` and are measured by the `--chart-ink on <surface>` rows above, because a
 * separator's ground is a fill while theirs is the card.
 */
const CHART_SERIES_ON_CARD: readonly ThemeTokenId[] = [
  '--chart-1',
  '--chart-2',
  '--chart-3',
  '--chart-4',
  '--chart-5',
  '--chart-6',
  '--chart-7',
  '--chart-8',
];

function chartRecords(): ThemePairRecord[] {
  const policy: PairPolicy = {
    kind: 'chart-separation',
    floor: AA_NON_TEXT,
    boundary: t('--chart-boundary'),
    cue: CHART_CUE,
  };
  const out: ThemePairRecord[] = [];
  CHART_SERIES_ON_CARD.forEach((series, index) => {
    out.push(
      record(
        `chart/chart ${index + 1} on white`,
        t(series),
        t('--card'),
        policy,
        'charts/chart-theme.ts fill against the card canvas',
      ),
    );
  });
  for (let index = 0; index + 1 < CHART_SERIES_ON_CARD.length; index += 1) {
    out.push(
      record(
        `chart/chart ${index + 1} adjacent to chart ${index + 2}`,
        t(CHART_SERIES_ON_CARD[index] as ThemeTokenId),
        t(CHART_SERIES_ON_CARD[index + 1] as ThemeTokenId),
        policy,
        'adjacent series in the fixed palette order',
      ),
    );
  }
  return out;
}

// ─── the navigation gradient ─────────────────────────────────────────────────

/**
 * `TopNavBar.tsx:229` runs `from-ss-black` to `to-chrome-gradient-end`. BOTH stops are editable
 * roles after A0, so the endpoint rows in `(d)` are no longer sufficient: the worst point of an
 * arbitrary editable ramp can sit strictly inside it. These records measure the minimum over the
 * actual sRGB interpolation. At stock the minimum lands on the far-stop endpoint, so they agree
 * with the endpoint rows today — the value is in what they catch after an edit.
 *
 * EIGHT foregrounds, not six. `--chrome-text` and `--chrome-text-muted` were added after the
 * independent web instrument was compared against this port: their only rows here are the
 * endpoint-only `(d) identity chrome text on the lightest gradient endpoint` and
 * `(d) default muted chrome item on the lightest gradient endpoint`, which structurally cannot
 * see an interior worst point — yet the web's own interior sweep
 * (`apps/web/src/styles/__tests__/contrast-registry.test.ts:328-345`) covers exactly
 * `--on-chrome`, `--chrome-text`, `--chrome-text-muted` and `--highlight` on this ramp, and
 * both stops are editable. The two endpoint `(d)` IDs are RETAINED: under the stable-ID rule
 * coverage is added beside them, never moved onto them, and their endpoint measurement is still
 * true. The four portal accents are here too because the bar paints them as active labels
 * (`DARK_CHROME`), which the web sweep does not cover — wider, not different.
 */
function gradientRecords(): ThemePairRecord[] {
  const background: ColourGradient = {
    gradient: { from: t('--ss-black'), to: t('--chrome-gradient-end') },
  };
  const rows: { foreground: ThemeTokenId; floor: number; note: string }[] = [
    { foreground: '--highlight', floor: AA_NON_TEXT, note: 'TopNavBar dark-bar focus ring' },
    { foreground: '--on-chrome', floor: AA_TEXT, note: "the bar's own label colour" },
    {
      foreground: '--chrome-text',
      floor: AA_TEXT,
      note: 'TopNavBar user identity text',
    },
    {
      foreground: '--chrome-text-muted',
      floor: AA_TEXT,
      note: 'TopNavBar default dark-navigation item',
    },
    ...PORTAL_ACCENTS.map((accent) => ({
      foreground: accent,
      floor: AA_TEXT,
      note: 'TopNavBar style={{ color: config.accentCssValue }} active label',
    })),
  ];
  return rows.map((row) =>
    record(
      `gradient/nav chrome/${row.foreground}`,
      t(row.foreground),
      background,
      { kind: 'gradient-minimum', floor: row.floor },
      `${row.note}; minimum over the whole TopNavBar.tsx:229 ramp`,
    ),
  );
}

// ─── the inventory ───────────────────────────────────────────────────────────

export const THEME_PAIRS: readonly ThemePairRecord[] = Object.freeze([
  ...siblingPairRecords(),
  ...statusBadgeRecords(),
  ...crossSurfaceRecords(),
  ...inventoryRecords(),
  ...statePairRecords(),
  ...onAccentRecords(),
  ...chartRecords(),
  ...gradientRecords(),
]);

/** `roleId -> pairIds` is computed in `theme.ts`, which owns the role catalogue. */
export const THEME_PAIR_IDS: readonly string[] = Object.freeze(THEME_PAIRS.map((pair) => pair.id));
